/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import i18n from '@app/I18n';
import type {ChannelRecord} from '@app/records/ChannelRecord';
import type {GuildMemberRecord} from '@app/records/GuildMemberRecord';
import type {GuildRecord} from '@app/records/GuildRecord';
import type {UserRecord} from '@app/records/UserRecord';
import GuildMemberStore from '@app/stores/GuildMemberStore';
import PresenceStore from '@app/stores/PresenceStore';
import * as NicknameUtils from '@app/utils/NicknameUtils';
import * as PermissionUtils from '@app/utils/PermissionUtils';
import {Permissions} from '@fluxer/constants/src/ChannelConstants';
import {StatusTypes} from '@fluxer/constants/src/StatusConstants';
import {msg} from '@lingui/core/macro';

export interface GuildMemberGroup {
	id: string;
	displayName: string;
	count: number;
	members: Array<GuildMemberRecord>;
	position: number;
}

export interface GroupDMMemberGroup {
	id: string;
	displayName: string;
	count: number;
	users: Array<UserRecord>;
}

function getVisibleMembers(guild: GuildRecord, channel: ChannelRecord): Array<GuildMemberRecord> {
	return GuildMemberStore.getMembers(guild.id).filter((member) =>
		PermissionUtils.can(Permissions.VIEW_CHANNEL, member.user, channel.toJSON()),
	);
}

function sortMembers(members: Array<GuildMemberRecord>, guildId: string): Array<GuildMemberRecord> {
	return [...members].sort((a, b) =>
		NicknameUtils.getNickname(a.user, guildId).localeCompare(NicknameUtils.getNickname(b.user, guildId)),
	);
}

function guildHasCustomHoistOrder(guild: GuildRecord): boolean {
	for (const role of Object.values(guild.roles)) {
		if (role.hoistPosition !== null) return true;
	}
	return false;
}

// effectiveHoistPosition on the role record falls back from hoist_position
// to position, but those are two unrelated scales (custom hoist order vs.
// role-list hierarchy). Mixing them — e.g. ranks were reordered into a
// custom hoist order, then costume roles were added later with no
// hoist_position — lets a null-hoist costume role outrank an explicitly-
// ordered rank role. Pick one scale per guild: if any role has a custom
// hoist_position, treat unset as the bottom of that scale; otherwise fall
// back to the plain role-list position uniformly.
function hoistKey(role: {hoistPosition: number | null; position: number}, useCustomScale: boolean): number {
	if (useCustomScale) return role.hoistPosition ?? Number.NEGATIVE_INFINITY;
	return role.position;
}

function getHighestHoistedRole(member: GuildMemberRecord, guild: GuildRecord, useCustomScale: boolean) {
	const hoistedRoles = [...member.roles].map((roleId) => guild.getRole(roleId)).filter((role) => role?.hoist);

	if (hoistedRoles.length === 0) return null;

	hoistedRoles.sort((a, b) => {
		const aPos = hoistKey(a!, useCustomScale);
		const bPos = hoistKey(b!, useCustomScale);
		if (bPos !== aPos) {
			return bPos - aPos;
		}
		return BigInt(a!.id) < BigInt(b!.id) ? -1 : 1;
	});

	const highestRole = hoistedRoles[0]!;
	return {
		id: highestRole.id,
		name: highestRole.name,
		hoistPosition: hoistKey(highestRole, useCustomScale),
	};
}

function groupMembersByRole(members: Array<GuildMemberRecord>, guild: GuildRecord): Array<GuildMemberGroup> {
	const useCustomScale = guildHasCustomHoistOrder(guild);
	const roleGroups: Record<string, Array<GuildMemberRecord>> = {};
	const roleHoistPositions: Record<string, number> = {};
	const roleNames: Record<string, string> = {};

	for (const member of members) {
		const highestRole = getHighestHoistedRole(member, guild, useCustomScale);
		if (!highestRole) continue;

		if (!roleGroups[highestRole.id]) {
			roleGroups[highestRole.id] = [];
			roleHoistPositions[highestRole.id] = highestRole.hoistPosition;
			roleNames[highestRole.id] = highestRole.name;
		}

		roleGroups[highestRole.id].push(member);
	}

	return Object.keys(roleGroups)
		.map((roleId) => ({
			id: roleId,
			displayName: roleNames[roleId],
			count: roleGroups[roleId].length,
			members: sortMembers(roleGroups[roleId], guild.id),
			position: roleHoistPositions[roleId],
		}))
		.sort((a, b) => {
			if (b.position !== a.position) {
				return b.position - a.position;
			}
			return BigInt(a.id) < BigInt(b.id) ? -1 : 1;
		});
}

function getOnlineWithoutHoistedRole(members: Array<GuildMemberRecord>, guild: GuildRecord): Array<GuildMemberRecord> {
	const useCustomScale = guildHasCustomHoistOrder(guild);
	return sortMembers(
		members.filter((member) => !getHighestHoistedRole(member, guild, useCustomScale)),
		guild.id,
	);
}

export function getMemberGroups(guild: GuildRecord, channel: ChannelRecord): Array<GuildMemberGroup> {
	const members = getVisibleMembers(guild, channel);

	const onlineMembers: Array<GuildMemberRecord> = [];
	const offlineMembers: Array<GuildMemberRecord> = [];

	for (const member of members) {
		const status = PresenceStore.getStatus(member.user.id);
		if (status === StatusTypes.OFFLINE || status === StatusTypes.INVISIBLE) {
			offlineMembers.push(member);
			continue;
		}
		onlineMembers.push(member);
	}

	const groupedMembers = groupMembersByRole(onlineMembers, guild);

	return [
		...groupedMembers,
		{
			id: 'online',
			displayName: i18n._(msg`Online`),
			count: onlineMembers.length - groupedMembers.reduce((acc, group) => acc + group.count, 0),
			members: getOnlineWithoutHoistedRole(onlineMembers, guild),
			position: 10000,
		},
		{
			id: 'offline',
			displayName: i18n._(msg`Offline`),
			count: offlineMembers.length,
			members: sortMembers(offlineMembers, guild.id),
			position: 20000,
		},
	].filter((group) => group.count > 0);
}

function sortUsersByDisplayName(users: Array<UserRecord>): Array<UserRecord> {
	return [...users].sort((a, b) => {
		const nameA = a.globalName ?? a.username;
		const nameB = b.globalName ?? b.username;
		return nameA.localeCompare(nameB);
	});
}

export function getGroupDMMemberGroups(users: Array<UserRecord>): Array<GroupDMMemberGroup> {
	const onlineUsers: Array<UserRecord> = [];
	const offlineUsers: Array<UserRecord> = [];

	for (const user of users) {
		const status = PresenceStore.getStatus(user.id);
		if (status === StatusTypes.OFFLINE || status === StatusTypes.INVISIBLE) {
			offlineUsers.push(user);
		} else {
			onlineUsers.push(user);
		}
	}

	return [
		{
			id: 'online',
			displayName: i18n._(msg`Online`),
			count: onlineUsers.length,
			users: sortUsersByDisplayName(onlineUsers),
		},
		{
			id: 'offline',
			displayName: i18n._(msg`Offline`),
			count: offlineUsers.length,
			users: sortUsersByDisplayName(offlineUsers),
		},
	].filter((group) => group.count > 0);
}
